import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DatePicker, Flex, Typography, theme } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import dayjs, { type Dayjs } from 'dayjs';

import {
    DEFAULT_DATE_RANGE_PRESETS,
    TODAY_BOUND,
    fixedBound,
    formatDateRangeLabel,
    getPresetDays,
    isValidDateRange,
    lastNDays,
    resolveDateRange,
    type DateBound,
    type DateRangeLabels,
    type DateRangeSpec,
} from './dateRangeSpec';
import Button from '../Button';

type Range = 'start' | 'end';

export type DateRangePickerProps = Omit<
    RangePickerProps,
    | 'value'
    | 'defaultValue'
    | 'onChange'
    | 'presets'
    | 'allowClear'
    | 'allowEmpty'
    | 'open'
    | 'onOpenChange'
    | 'onFocus'
    | 'onCalendarChange'
    | 'panelRender'
    | 'renderExtraFooter'
> & {
    /** Current range; `null` renders an empty picker. */
    value: DateRangeSpec | null;
    /** Called with the new spec; not called when the range is invalid. */
    onChange: (spec: DateRangeSpec) => void;
    /** "Last N Days" shortcuts shown beside the calendar. */
    presets?: number[];
    /** All user-facing copy, so the primitive stays i18n-free. */
    labels: DateRangeLabels;
};

const DateRangePicker = ({
    value,
    onChange,
    presets = DEFAULT_DATE_RANGE_PRESETS,
    labels,
    ...props
}: DateRangePickerProps) => {
    const { token } = theme.useToken();
    const [open, setOpen] = useState(false);
    const [hasInvalidPick, setHasInvalidPick] = useState(false);
    const isOpen = useRef(false);
    const activeRange = useRef<Range>('start');
    const pickedStart = useRef<Dayjs | null>(null);

    // Keyed by content: the parent may rebuild an equal spec every render.
    const valueKey = JSON.stringify(value);
    // Resolved once per value (never on a timer) so a tab left open past
    // midnight keeps its range until the next load or user action.
    const today = useMemo(() => dayjs(), [valueKey]);
    const resolved = value ? resolveDateRange(value, today) : null;
    const isInvalid =
        hasInvalidPick || (!!value && !isValidDateRange(value, today));
    const presetDays = value ? getPresetDays(value, presets) : undefined;

    useEffect(() => setHasInvalidPick(false), [valueKey]);

    const changeOpen = (next: boolean) => {
        isOpen.current = next;
        pickedStart.current = null;
        setOpen(next);
    };

    const commit = (spec: DateRangeSpec) => {
        changeOpen(false);
        const isValid = isValidDateRange(spec);
        setHasInvalidPick(!isValid);
        if (isValid) onChange(spec);
    };

    // A start picked on the calendar freezes as fixed; an untouched one keeps
    // its current type.
    const startBound = (): DateBound =>
        pickedStart.current
            ? fixedBound(pickedStart.current)
            : value?.start ?? TODAY_BOUND;

    // A fixed end also freezes an untouched dynamic start at the date it
    // shows; otherwise the start keeps sliding and passes the end.
    const startBoundForFixedEnd = (): DateBound =>
        !pickedStart.current && value?.start.type === 'dynamic'
            ? fixedBound(resolveDateRange(value, today)[0])
            : startBound();

    // AntD skips `onChange` when the picked end equals the current end, so an
    // end picked on the calendar is committed from the calendar event to let
    // it freeze as fixed even when it is today. AntD also replays calendar
    // events while closing, hence the open guard.
    const handleCalendarChange: RangePickerProps['onCalendarChange'] = (
        dates,
        _strings,
        info,
    ) => {
        const date = dates[info.range === 'start' ? 0 : 1];
        if (!isOpen.current || !date) return;
        if (info.range === 'start') {
            pickedStart.current = date;
            return;
        }
        // Two calendar picks in reverse order are swapped, like AntD does.
        const start = pickedStart.current;
        if (start && date.isBefore(start, 'day')) {
            commit({ start: fixedBound(date), end: fixedBound(start) });
            return;
        }
        commit({ start: startBoundForFixedEnd(), end: fixedBound(date) });
    };

    // Only reached with a picked start and no picked end: it runs through Today.
    const handleChange = () => {
        if (pickedStart.current)
            commit({ start: startBound(), end: TODAY_BOUND });
    };

    const handleToday = () =>
        commit({
            start: activeRange.current === 'start' ? TODAY_BOUND : startBound(),
            end: TODAY_BOUND,
        });

    return (
        <Flex align="center" gap={token.marginSM} wrap>
            <DatePicker.RangePicker
                allowEmpty={[false, true]}
                allowClear={false}
                status={isInvalid ? 'error' : undefined}
                {...props}
                value={resolved}
                open={open}
                onOpenChange={changeOpen}
                onFocus={(_event, info) => {
                    activeRange.current = info.range;
                }}
                onCalendarChange={handleCalendarChange}
                onChange={handleChange}
                panelRender={panel => (
                    <Flex>
                        <Flex
                            vertical
                            style={{
                                padding: token.paddingXS,
                                borderRight: `${token.lineWidth}px ${token.lineType} ${token.colorSplit}`,
                            }}
                        >
                            {presets.map(days => (
                                <Button
                                    key={days}
                                    type={
                                        days === presetDays ? 'primary' : 'link'
                                    }
                                    onClick={() => commit(lastNDays(days))}
                                >
                                    {labels.lastNDays(days)}
                                </Button>
                            ))}
                        </Flex>
                        {panel}
                    </Flex>
                )}
                renderExtraFooter={() => (
                    <Button size="small" type="link" onClick={handleToday}>
                        {labels.today}
                    </Button>
                )}
            />
            {isInvalid ? (
                <Typography.Text role="alert" type="danger">
                    {labels.endBeforeStart}
                </Typography.Text>
            ) : (
                value && (
                    <Typography.Text type="secondary">
                        {formatDateRangeLabel(value, labels, today, presets)}
                    </Typography.Text>
                )
            )}
        </Flex>
    );
};

DateRangePicker.displayName = 'DateRangePicker';

export default DateRangePicker;
